import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';

import { _nextTrackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { TRIGGER_OBJECT } from '../../constants/ClickstreamConstants';

export const EVENT_IDS = {
  INCOME_RENT_RATIO: '6608',
  INCOME_RENT_NEGOTIABLE: '6609',
  CREDIT_SCORE: '6610',
  CREDIT_SCORE_NEGOTIABLE: '6611',
  NUMBER_OF_CATS_ALLOWED: '6614',
  NUMBER_OF_LARGE_DOGS_ALLOWED: '6616',
  NUMBER_OF_SMALL_DOGS_ALLOWED: '6615',
  DOG_SIZE_ALLOWED: '6619',
  NUMBER_OF_DOGS_ALLOWED: '6618',
  PET_POLICY_NEGOTIABLE: '6617',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6608
export const emitIncomeRatioEvent = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.INCOME_RENT_RATIO,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'income_rent_ratio_field',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6609
export const emitIncomeRentNegotiableEvent = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.INCOME_RENT_NEGOTIABLE,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'income_rent_ratio_negotiable',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6610
export const emitCreditScoreEvent = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CREDIT_SCORE,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'min_credit_score_select',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6611
export const emitCreditScoreNegotiableEvent = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CREDIT_SCORE_NEGOTIABLE,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'credit_score_negotiable_checkbox',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6619
export const emitDogSizeAllowedEvent = ({
  currentProperty,
  user,
  triggerLocation = 'post_activation',
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation?: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DOG_SIZE_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'dog_size_allowed',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6616
export const emitNumberOfLargeDogsAllowedEvent = ({
  currentProperty,
  user,
  triggerObject,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_LARGE_DOGS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'number_of_large_dogs',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6615
export const emitNumberOfSmallDogsAllowedEvent = ({
  currentProperty,
  user,
  triggerObject,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_SMALL_DOGS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'number_of_small_dogs',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6618
export const emitNumberOfDogsAllowedEvent = ({
  currentProperty,
  user,
  triggerLocation = 'post_activation',
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation?: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_DOGS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'number_of_dogs_allowed',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6614
export const emitNumberOfCatsAllowedEvent = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NUMBER_OF_CATS_ALLOWED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'number_of_cats_allowed',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6617
export const emitPetPolicyNegotiableEvent = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = 'no_trigger_object',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  triggerLocation: string;
  triggerObject?: string;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PET_POLICY_NEGOTIABLE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'pets_negotiable_checkbox',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
